import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useAlert } from './AlertContext';
import { useNavigate } from 'react-router-dom';
import { AWS_CONFIG } from "../config/aws-config";

// Types for API data
export interface AllRequirements {
    firstName: string; // Used to figure out if user has completed profile
    requirements: Requirement[];
    companyRequirements: Requirement[];
    pendingRequirements: Requirement[];
    expiredRequirements: Requirement[];
    rejectedRequirements: Requirement[];
    pendingCompanyRequirements: Requirement[];
    expiredCompanyRequirements: Requirement[];
    rejectedCompanyRequirements: Requirement[];
}

export interface Requirement {
    client_requirement_id: string;
    requirement_id: string;
    requirement_name: string;
    description: string;
    signed: boolean;
    status?: string;
    status_comment?: string;
    acknowledgement_tick_boxes: AcknowledgementTickBox[];
    content: string;
    form_fields: {
        upload_document?: FormField;
        issued_date?: FormField;
        expiry_date?: FormField;
        i_am_exempt?: FormField;
        exempt_reason?: FormField;
        expires_in?: FormField;
    };
    custom_form_fields: CustomFormField[];
    client_type: string;
    expiry_date_indexed?: string;
}

export interface AcknowledgementTickBox {
    required: boolean;
    id: number;
    text: string;
    checked?: boolean;
}

export interface FormField {
    display_name: string;
    enabled: boolean;
    required: boolean;
    value?: any;
    duration?: number;
    unit?: string;
    exempt_reason?: string;
}

export interface CustomFormField {
    name: string;
    id: string;
    type: string;
    enabled: boolean;
    required: boolean;
    value?: string;
    options?: string[];
    include_other?: boolean;
}

export interface ProfileFieldValue {
    profile_field_id: string;
    format: 'text' | 'number' | 'yes_no' | 'date' | 'dropdown_single' | 'dropdown_multi';
    order: number;
    required: boolean;
    label?: string;
    value?: string;
    options?: string[] | string;
}

export interface Company {
    company_id?: string;
    company_name: string;
    primary_contact: string;
    email: string;
    phone_number?: string;
    profile_field_values?: ProfileFieldValue[] | { [key: string]: string };
}

export interface UserProfile {
    first_name: string;
    last_name: string;
    other_names: string;
    date_of_birth: string;
    mobile: string;
    email: string;
}

export interface Customer {
    customer_code: string;
    customer_name: string;
    person_type: string;
    people_id: string;
    company_id: string;
}

// Cache state interface
interface ApiCache {
    userProfile: UserProfile | null;
    allRequirements: AllRequirements | null;
    customers: Customer[] | null;
    company: Company | null;
    lastFetch: {
        userProfile: number | null;
        allRequirements: number | null;
        customers: number | null;
        company: number | null;
    };
}

interface ApiContextType {
    // Data
    userProfile: UserProfile | null;
    allRequirements: AllRequirements | null;
    customers: Customer[] | null;
    
    // Loading states
    loadingProfile: boolean;
    loadingRequirements: boolean;
    loadingCustomers: boolean;
    
    // Methods
    fetchUserProfile: (forceRefresh?: boolean) => Promise<UserProfile | null>;
    fetchRequirements: (forceRefresh?: boolean) => Promise<AllRequirements | null>;
    fetchCustomers: (forceRefresh?: boolean) => Promise<Customer[] | null>;
    fetchCompany: (forceRefresh?: boolean) => Promise<Company | null>;
    saveCompany: (companyData: Omit<Company, 'company_id'>) => Promise<boolean>;
    updateUserProfile: (profileData: Partial<UserProfile>) => Promise<boolean>;
    submitRequirement: (requirementId: string, formData: FormData, clientType: string) => Promise<boolean>;
    clearCache: () => void;
    invalidateCache: (key: keyof ApiCache['lastFetch']) => void;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const useApi = (): ApiContextType => {
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error('useApi must be used within an ApiProvider');
    }
    return context;
};

interface ApiProviderProps {
    children: ReactNode;
}

const BASE_URL = AWS_CONFIG.apiBaseUrl;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
    const { user, logout } = useAuth();
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    // Cache state
    const [cache, setCache] = useState<ApiCache>({
        userProfile: null,
        allRequirements: null,
        customers: null,
        company: null,
        lastFetch: {
            userProfile: null,
            allRequirements: null,
            customers: null,
            company: null,
        },
    });

    // Loading states
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [loadingRequirements, setLoadingRequirements] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);

    // Helper function to check if cache is valid
    const isCacheValid = (lastFetchTime: number | null): boolean => {
        if (!lastFetchTime) return false;
        return Date.now() - lastFetchTime < CACHE_DURATION;
    };

    // Helper function to handle 401 errors
    const handle401Error = useCallback(() => {
        showAlert('Your session has expired. Please login again.', 'error');
        sessionStorage.clear();
        if (logout) logout();
        setTimeout(() => navigate('/'), 2000);
    }, [showAlert, logout, navigate]);

    // Helper function to get auth headers
    const getAuthHeaders = () => {
        const idToken = sessionStorage.getItem('idToken');
        return {
            'Authorization': idToken || '',
            'Content-Type': 'application/json',
        };
    };

    // Helper function to get user email
    const getUserEmail = () => {
        return user?.email || sessionStorage.getItem('profileEmail') || sessionStorage.getItem('userEmail');
    };

    // Helper function to get visitor params
    const getVisitorParams = () => {
        const personType = sessionStorage.getItem('personType');
        const peopleId = sessionStorage.getItem('peopleId');
        const customerCode = sessionStorage.getItem('customerCode');
        return { customerCode, peopleId, personType };
    };

    // Helper function to get company params
    const getCompanyParams = () => {
        const companyId = sessionStorage.getItem('companyId');
        return { companyId };
    };

    // Fetch User Profile
    const fetchUserProfile = useCallback(async (forceRefresh = false): Promise<UserProfile | null> => {
        // Return cached data if valid and not forcing refresh
        if (!forceRefresh && cache.userProfile && isCacheValid(cache.lastFetch.userProfile)) {
            return cache.userProfile;
        }

        const email = getUserEmail();
        const { customerCode, peopleId, personType } = getVisitorParams();

        if (!email) {
            return null;
        }

        setLoadingProfile(true);

        try {
            const response = await fetch(`${BASE_URL}/webV2/visitorportal/user/${encodeURIComponent(email)}?customer_code=${customerCode}&people_id=${peopleId}&person_type=${personType}`, {
                method: 'GET',
                headers: getAuthHeaders(),
            });

            if (response.status === 401) {
                handle401Error();
                return null;
            }

            if (response.ok) {
                const result = await response.json();
                // console.log('User profile result:', result);
                const userData = result.returnData || result;
                
                const profile: UserProfile = {
                    first_name: userData.first_name || '',
                    last_name: userData.last_name || '',
                    other_names: userData.other_names || '',
                    date_of_birth: userData.date_of_birth || '',
                    mobile: userData.mobile || '',
                    email: email,
                };

                setCache(prev => ({
                    ...prev,
                    userProfile: profile,
                    lastFetch: { ...prev.lastFetch, userProfile: Date.now() },
                }));

                return profile;
            }

            return null;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            if (error instanceof TypeError && (
                error.message === 'Failed to fetch' ||
                error.message.includes('NetworkError') ||
                error.message.includes('Network request failed')
            )) {
                handle401Error();
            }
            return null;
        } finally {
            setLoadingProfile(false);
        }
    }, [cache.userProfile, cache.lastFetch.userProfile, user, handle401Error]);

    // Fetch Requirements
    const fetchRequirements = useCallback(async (forceRefresh = false): Promise<AllRequirements | null> => {

        if (!forceRefresh && cache.allRequirements && isCacheValid(cache.lastFetch.allRequirements)) {
            return cache.allRequirements;
        }

        const email = getUserEmail();
        const { customerCode, peopleId, personType } = getVisitorParams();
        const { companyId } = getCompanyParams();

        if (!email) {
            return null;
        }

        setLoadingRequirements(true);

        try {
            const response = await fetch(
                `${BASE_URL}/webV2/visitorportal/user/${encodeURIComponent(email)}?customer_code=${customerCode}&people_id=${peopleId}&person_type=${personType}&company_id=${companyId}`,
                {
                    method: 'GET',
                    headers: getAuthHeaders(),
                }
            );

            if (response.status === 401) {
                handle401Error();
                return null;
            }

            if (response.ok) {
                const result = await response.json();

                if (!result.returnData.first_name && peopleId !== 'undefined') {
                    navigate('/profile')
                }

                // console.log('Requirements result:', result);
                const requirementsList: Requirement[] = result.returnData.requirements || [];

                // Filter to only include unsigned requirements (signed === false)
                const pendingDocs: Requirement[] = requirementsList.filter(doc => !doc.signed);
                // console.log('Pending requirements (unsigned only):', pendingDocs);

                const expiredDocs: Requirement[] = requirementsList.filter(doc => doc.status === 'expired');
                // console.log('Expired requirements:', expiredDocs);

                const rejectedDocs: Requirement[] = requirementsList.filter(doc => doc.status === 'rejected');
                // console.log('Rejected requirements:', rejectedDocs);

                const requirementCompanyList: Requirement[] = result.returnData.company_requirements || [];

                // Filter to only include unsigned requirements (signed === false)
                const pendingCompanyDocs: Requirement[] = requirementCompanyList.filter(doc => !doc.signed);
                // console.log('Pending company requirements (unsigned only):', pendingCompanyDocs);

                const expiredCompanyDocs: Requirement[] = requirementCompanyList.filter(doc => doc.status === 'expired');
                // console.log('Expired company requirements:', expiredCompanyDocs);

                const rejectedCompanyDocs: Requirement[] = requirementCompanyList.filter(doc => doc.status === 'rejected');
                // console.log('Rejected company requirements:', rejectedCompanyDocs);

                const allRequirements = {
                    firstName: result.returnData.first_name || '',
                    requirements: requirementsList,
                    companyRequirements: requirementCompanyList,
                    pendingRequirements: pendingDocs,
                    expiredRequirements: expiredDocs,
                    rejectedRequirements: rejectedDocs,
                    pendingCompanyRequirements: pendingCompanyDocs,
                    expiredCompanyRequirements: expiredCompanyDocs,
                    rejectedCompanyRequirements: rejectedCompanyDocs,
                };

                setCache(prev => ({
                    ...prev,
                    allRequirements: allRequirements,
                    lastFetch: { ...prev.lastFetch, allRequirements: Date.now() },
                }));

                return allRequirements;
            }

            return null;
        } catch (error) {
            console.error('Error fetching requirements:', error);
            if (error instanceof TypeError && (
                error.message === 'Failed to fetch' ||
                error.message.includes('NetworkError') ||
                error.message.includes('Network request failed')
            )) {
                handle401Error();
            }
            return null;
        } finally {
            setLoadingRequirements(false);
        }
    }, [cache.allRequirements, cache.lastFetch.allRequirements, user, handle401Error]);

    // Fetch Customers
    const fetchCustomers = useCallback(async (forceRefresh = false): Promise<Customer[] | null> => {
        if (!forceRefresh && cache.customers && isCacheValid(cache.lastFetch.customers)) {
            return cache.customers;
        }

        const email = getUserEmail();

        if (!email) {
            return null;
        }

        setLoadingCustomers(true);

        try {
            const response = await fetch(`${BASE_URL}/webV2/visitorportal/user/${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: getAuthHeaders(),
            });

            if (response.status === 401) {
                handle401Error();
                return null;
            }

            if (response.ok) {
                const result = await response.json();
                // console.log('Customers result:', result);
                const customersList: Customer[] = result.returnData.customer_list || [];

                setCache(prev => ({
                    ...prev,
                    customers: customersList,
                    lastFetch: { ...prev.lastFetch, customers: Date.now() },
                }));

                return customersList;
            }

            return [];
        } catch (error) {
            console.error('Error fetching customers:', error);
            if (error instanceof TypeError && (
                error.message === 'Failed to fetch' ||
                error.message.includes('NetworkError') ||
                error.message.includes('Network request failed')
            )) {
                handle401Error();
            }
            return [];
        } finally {
            setLoadingCustomers(false);
        }
    }, [cache.customers, cache.lastFetch.customers, user, handle401Error]);

    // Fetch Company
    const fetchCompany = useCallback(async (forceRefresh = false): Promise<Company | null> => {
        if (!forceRefresh && cache.company && isCacheValid(cache.lastFetch.company)) {
            return cache.company;
        }

        const customerCode = sessionStorage.getItem('customerCode');
        const companyId = sessionStorage.getItem('companyId');
        const idToken = sessionStorage.getItem('idToken');

        if (!companyId || !idToken) return null;

        try {
            const response = await fetch(
                `${BASE_URL}/webV2/visitorportal/company/${companyId}?customer_code=${customerCode}`,
                { method: 'GET', headers: getAuthHeaders() }
            );

            if (response.status === 401) {
                handle401Error();
                return null;
            }

            if (response.ok) {
                const result = await response.json();
                const company: Company = result.returnData || result;

                setCache(prev => ({
                    ...prev,
                    company,
                    lastFetch: { ...prev.lastFetch, company: Date.now() },
                }));

                return company;
            }

            return null;
        } catch (error) {
            console.error('Error fetching company:', error);
            if (error instanceof TypeError && (
                error.message === 'Failed to fetch' ||
                error.message.includes('NetworkError') ||
                error.message.includes('Network request failed')
            )) {
                handle401Error();
            }
            return null;
        }
    }, [cache.company, cache.lastFetch.company, handle401Error]);

    // Save Company (create or update)
    const saveCompany = useCallback(async (companyData: Omit<Company, 'company_id'>): Promise<boolean> => {
        const customerCode = sessionStorage.getItem('customerCode');
        const companyId = sessionStorage.getItem('companyId');
        const idToken = sessionStorage.getItem('idToken');

        try {
            let response;
            if (companyId && companyId !== 'undefined') {
                response = await fetch(`${BASE_URL}/webV2/visitorportal/company/${companyId}`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ customer_code: customerCode, ...companyData }),
                });
            } else {
                response = await fetch(`${BASE_URL}/webV2/visitorportal/company`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ customer_code: customerCode, ...companyData }),
                });
            }

            if (response.status === 401) {
                handle401Error();
                return false;
            }

            if (response.ok) {
                const result = await response.json();
                const responseData = result.returnData || result;

                if (responseData.company_id) {
                    sessionStorage.setItem('companyId', responseData.company_id);
                }

                // Invalidate company cache
                setCache(prev => ({
                    ...prev,
                    company: null,
                    lastFetch: { ...prev.lastFetch, company: null },
                }));

                showAlert('Company information saved successfully!', 'success');
                return true;
            }

            showAlert('Failed to save company information', 'error');
            return false;
        } catch (error) {
            console.error('Error saving company:', error);
            if (error instanceof TypeError && (
                error.message === 'Failed to fetch' ||
                error.message.includes('NetworkError') ||
                error.message.includes('Network request failed')
            )) {
                handle401Error();
            } else {
                showAlert('Error saving company information. Please try again.', 'error');
            }
            return false;
        }
    }, [showAlert, handle401Error]);

    // Update User Profile
    const updateUserProfile = useCallback(async (profileData: Partial<UserProfile>): Promise<boolean> => {
        const email = getUserEmail();
        const { customerCode, peopleId, personType } = getVisitorParams();

        if (!email) {
            showAlert('User email not found', 'error');
            return false;
        }

        try {
            const response = await fetch(
                `${BASE_URL}/webV2/visitorportal/user/${encodeURIComponent(email)}?customer_code=${customerCode}&client_id=${peopleId}`,
                {
                    method: 'PUT',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(profileData),
                }
            );

            if (response.status === 401) {
                handle401Error();
                return false;
            }

            if (response.ok) {
                // Invalidate profile cache to force refresh on next fetch
                setCache(prev => ({
                    ...prev,
                    userProfile: null,
                    lastFetch: { ...prev.lastFetch, userProfile: null },
                }));

                showAlert('Profile updated successfully!', 'success');
                return true;
            }

            showAlert('Failed to update profile', 'error');
            return false;
        } catch (error) {
            console.error('Error updating profile:', error);
            if (error instanceof TypeError && (
                error.message === 'Failed to fetch' ||
                error.message.includes('NetworkError') ||
                error.message.includes('Network request failed')
            )) {
                handle401Error();
            } else {
                showAlert('Error updating profile. Please try again.', 'error');
            }
            return false;
        }
    }, [user, showAlert, handle401Error]);

    // Submit Requirement
    const submitRequirement = useCallback(async (requirementId: string, formData: FormData, clientType: string): Promise<boolean> => {
        const email = getUserEmail();
        const { customerCode, peopleId, personType } = getVisitorParams();
        const { companyId } = getCompanyParams();

        const clientId = (clientType === "company") ? companyId : peopleId;

        if (!email) {
            showAlert('User email not found', 'error');
            return false;
        }

        try {
            const response = await fetch(
                `${BASE_URL}/webV2/visitorportal/user/${encodeURIComponent(email)}/requirement?customer_code=${customerCode}&client_id=${clientId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': sessionStorage.getItem('idToken') || '',
                    },
                    body: formData,
                }
            );

            if (response.status === 401) {
                handle401Error();
                return false;
            }

            if (response.ok) {
                // Invalidate requirements cache to force refresh
                setCache(prev => ({
                    ...prev,
                    allRequirements: null,
                    lastFetch: {
                        ...prev.lastFetch,
                        allRequirements: null
                    },
                }));

                showAlert('Requirement submitted successfully!', 'success');
                return true;
            }

            showAlert('Failed to submit requirement', 'error');
            return false;
        } catch (error) {
            console.error('Error submitting requirement:', error);
            if (error instanceof TypeError && (
                error.message === 'Failed to fetch' ||
                error.message.includes('NetworkError') ||
                error.message.includes('Network request failed')
            )) {
                handle401Error();
            } else {
                showAlert('Error submitting requirement. Please try again.', 'error');
            }
            return false;
        }
    }, [user, showAlert, handle401Error]);

    // Clear all cache
    const clearCache = useCallback(() => {
        setCache({
            userProfile: null,
            allRequirements: null,
            customers: null,
            company: null,
            lastFetch: {
                userProfile: null,
                allRequirements: null,
                customers: null,
                company: null,
            },
        });
    }, []);

    // Invalidate specific cache
    const invalidateCache = useCallback((key: keyof ApiCache['lastFetch']) => {
        setCache(prev => ({
            ...prev,
            [key]: null,
            lastFetch: { ...prev.lastFetch, [key]: null },
        }));
    }, []);

    const value: ApiContextType = {
        // Data
        userProfile: cache.userProfile,
        allRequirements: cache.allRequirements,
        customers: cache.customers,

        // Loading states
        loadingProfile,
        loadingRequirements,
        loadingCustomers,

        // Methods
        fetchUserProfile,
        fetchRequirements,
        fetchCustomers,
        fetchCompany,
        saveCompany,
        updateUserProfile,
        submitRequirement,
        clearCache,
        invalidateCache,
    };

    return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

